import React, { JSX, MouseEvent, useEffect, useMemo, useState } from 'react';
import classes from './calendar.module.scss';
import arrowLeft from '../../../shared/lib/assets/ArrowLeft.png'
import arrowRight from '../../../shared/lib/assets/ArrowRight.png'
import { MyButton } from '../button';

interface CalendarProps {
    isLoading?: boolean;
    onDateRangeSelect?: (startDate: Date | null, endDate: Date | null) => void;
    oneDate?: boolean;
    compact?: boolean;
    selectedStartDate?: Date | null;
    selectedEndDate?: Date | null;
}

const MSK_TIME_ZONE = 'Europe/Moscow';
const MSK_OFFSET_IN_MS = 3 * 60 * 60 * 1000;

const createUtcDateForMskMidnight = (year: number, month: number, day: number) => {
    return new Date(Date.UTC(year, month, day, 0, 0, 0) - MSK_OFFSET_IN_MS);
}

const getCalendarPartsInTimeZone = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);

    return {
        year: Number(parts.find(part => part.type === 'year')?.value),
        month: Number(parts.find(part => part.type === 'month')?.value) - 1,
        day: Number(parts.find(part => part.type === 'day')?.value)
    };
}

const getMskNow = () => {
    const { year, month, day } = getCalendarPartsInTimeZone(new Date(), MSK_TIME_ZONE);
    return createUtcDateForMskMidnight(year, month, day);
}

const normalizeToUtcMskMidnight = (date: Date) => {
    const { year, month, day } = getCalendarPartsInTimeZone(date, MSK_TIME_ZONE);
    return createUtcDateForMskMidnight(year, month, day);
}

const addMonthsInMsk = (date: Date, months: number) => {
    const { year, month } = getCalendarPartsInTimeZone(date, MSK_TIME_ZONE);
    return createUtcDateForMskMidnight(year, month + months, 1);
}

const getWeekdayInMsk = (date: Date) => {
    return new Date(date.getTime() + MSK_OFFSET_IN_MS).getUTCDay();
}

export const Calendar: React.FC<CalendarProps> = ({onDateRangeSelect, isLoading, oneDate, compact, selectedStartDate, selectedEndDate}) => {
    const initialDate = selectedStartDate ? normalizeToUtcMskMidnight(selectedStartDate) : null
    const initialEndDate = selectedEndDate ? normalizeToUtcMskMidnight(selectedEndDate) : null
    const [currentDate, setCurrentDate] = useState<Date>(() => initialDate || getMskNow());
    const [startDate, setStartDate] = useState<Date | null>(initialDate);
    const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [open, setOpen] = useState<boolean>(false)

    const getMonthCount = () => {
        if (compact) {
            return 1
        }
        return window.innerWidth < 650 ? 1 : window.innerWidth < 950 ? 2 : 3
    }

    const [monthCount, setMonthCount] = useState<1 | 2 | 3>(() => getMonthCount())

    useEffect(() => {
        const setMonthCountWrap = () => {
            setMonthCount(getMonthCount())
        }

        window.addEventListener('resize', setMonthCountWrap)

        return () => window.removeEventListener('resize', setMonthCountWrap)
    }, [])

    useEffect(() => {
        const normalizedStartDate = selectedStartDate ? normalizeToUtcMskMidnight(selectedStartDate) : null
        const normalizedEndDate = selectedEndDate ? normalizeToUtcMskMidnight(selectedEndDate) : null
        setStartDate(normalizedStartDate)
        setEndDate(normalizedEndDate)
        setHoverDate(null)
        setCurrentDate(normalizedStartDate || getMskNow())
    }, [selectedStartDate, selectedEndDate])

    const today = getMskNow()

    // Функции для навигации
    const prevMonth = (): void => {
        setCurrentDate(addMonthsInMsk(currentDate, -1));
    };

    const nextMonth = (): void => {
        setCurrentDate(addMonthsInMsk(currentDate, 1));
    };

    // Получение дат для трех месяцев
    const getThreeMonths = (): Date[] => {
        const months: Date[] = [];
        const interval = (oneDate || compact) ? [0, 0] : monthCount === 3 ? [-1, 1] : monthCount === 2 ? [-1, 0] : [0, 0]
        for (let i = interval[0]; i <= interval[1]; i++) {
            const monthDate = addMonthsInMsk(currentDate, i);
            months.push(monthDate);
        }
        return months;
    };

    // Проверка, находится ли дата в выбранном диапазоне
    const isInRange = (date: Date): boolean => {
        if (!startDate || !endDate) return false;
        return date > startDate && date < endDate;
    };

    // Проверка, является ли дата началом или концом диапазона
    const isRangeEdge = (date: Date): boolean => {
        if (!startDate || !endDate) return false;
        return date.getTime() === startDate.getTime() || date.getTime() === endDate.getTime();
    };

    const getUtcDateForMskMidnight = (date: Date) => {
        return normalizeToUtcMskMidnight(date)
    }

    // Обработчик клика по дате
    const handleDateClick = (date: Date): void => {
        if(oneDate){
            const newStartDate: Date | null = date;
            setStartDate(newStartDate);
            setHoverDate(null);
            setOpen(false)
            if(onDateRangeSelect) {
                onDateRangeSelect(getUtcDateForMskMidnight(newStartDate), null);
            }
            return
        }

        let newStartDate: Date | null = startDate;
        let newEndDate: Date | null = endDate;

        if (!startDate || (startDate && endDate)) {
            newStartDate = date;
            newEndDate = null;
        } else if (date >= startDate) {
            newEndDate = date;
        } else {
            newStartDate = date;
            newEndDate = null;
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setHoverDate(null);

        if(newStartDate && newEndDate){
            setOpen(false)
        }

        if (onDateRangeSelect) {
            onDateRangeSelect(newStartDate && getUtcDateForMskMidnight(newStartDate), newEndDate && getUtcDateForMskMidnight(newEndDate));
        }
    };

    // Обработчик наведения на дату
    const handleDateHover = (date: Date): void => {
        if (startDate && !endDate) {
            setHoverDate(date);
        }
    };

    // Генерация календаря для месяца
    const renderMonth = (monthDate: Date): JSX.Element[] => {
        const { year, month } = getCalendarPartsInTimeZone(monthDate, MSK_TIME_ZONE);

        const firstDay = createUtcDateForMskMidnight(year, month, 1);
        const lastDay = createUtcDateForMskMidnight(year, month + 1, 0);
        const daysInMonth = getCalendarPartsInTimeZone(lastDay, MSK_TIME_ZONE).day;

        const startDay = (getWeekdayInMsk(firstDay) + 6) % 7;  // en -> ru
        const days: JSX.Element[] = [];

        // Пустые ячейки перед первым днем месяца
        for (let i = 0; i < startDay; i++) {
            days.push(<section key={`empty-${i}`} className={classes.calendarDay + " " + classes.empty}></section>);
        }

        // Дни месяца
        for (let day = 1; day <= daysInMonth; day++) {
            const date = createUtcDateForMskMidnight(year, month, day);
            const isStart = startDate && date.getTime() === startDate.getTime();
            const isEnd = endDate && date.getTime() === endDate.getTime();
            const isHoverEnd = hoverDate && !endDate && startDate && date.getTime() === hoverDate.getTime();
            const inRange = isInRange(date);
            const isRangeEdgeDate = isRangeEdge(date);
            const isToday = date.getTime() === today.getTime();

            const dayClass = [
                classes.calendarDay,
                isStart && classes.startDate,
                isEnd && classes.endDate,
                inRange && classes.inRange,
                isRangeEdgeDate && classes.rangeEdge,
                isHoverEnd && classes.hoverEnd,
                isToday && classes.today
            ].filter(Boolean).join(' ');

            days.push(
                <section
                    key={date.toISOString()}
                    className={dayClass}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => handleDateHover(date)}
                >
                    {day}
                </section>
            );
        }

        return days;
    };

    const setToday = () => {
        const targetDate = getMskNow()
        setStartDate(targetDate)
        setEndDate(oneDate ? null : targetDate)
        onDateRangeSelect && onDateRangeSelect(getUtcDateForMskMidnight(targetDate), oneDate ? null : getUtcDateForMskMidnight(targetDate));
    }

    const onOpen = (e: MouseEvent) => {
        if(!isLoading){
            const target = e.target as Element;
            if(target.closest(`.${classes.today}`)){
                setOpen(false)
                setToday()
            }
            else{
                setOpen(!open)
            }
        }
    }

    const formatCalendarDate = (date: Date) => {
        return getUtcDateForMskMidnight(date).toLocaleDateString('ru-RU', { timeZone: MSK_TIME_ZONE });
    }

    const formatMonthLabel = (date: Date) => {
        return date.toLocaleString('ru-RU', { month: 'long', year: 'numeric', timeZone: MSK_TIME_ZONE })
    }

    const months = useMemo(getThreeMonths, [monthCount, currentDate, oneDate, compact])

    return (
        <section data-calendar-open={open} className={classes.calendarContainer + (oneDate ? ` ${classes.oneDate}` : '') + (compact ? ` ${classes.compact}` : '') + (open ? ` ${classes.open}` : '')}>
            <section
                onMouseDown={e => e.preventDefault()}
                onClick={onOpen}
                className={classes.selectedRange + (isLoading ? ` ${classes.disabled}` : '')}
            >
                <p>
                    {
                        oneDate
                            ?
                        <>
                            Дата: {!startDate ? 'Выберите дату' : formatCalendarDate(startDate)}
                        </>
                            :
                        <>
                            Период: {(!startDate && !endDate) ? 'Выберите даты (мск)' :
                            (startDate && !endDate) ? 'Выберите вторую дату' :
                            startDate && endDate && `${formatCalendarDate(startDate)} - ${formatCalendarDate(endDate)} (мск)`}
                        </>
                    }
                </p>
                <section className={classes.today}>
                    <MyButton>
                        Сегодня
                    </MyButton>
                </section>
            </section>
            {
                open
                    &&
                <section className={classes.wrapper}>
                    <section className={classes.calendarHeader}>
                        <button onClick={prevMonth} className={classes.navButton}>
                            <img src={arrowLeft} alt="Предыдущий месяц" />
                        </button>
                        <section className={classes.currentMonth}>
                            {formatMonthLabel(currentDate)}
                        </section>
                        <button onClick={nextMonth} className={classes.navButton}>
                            <img src={arrowRight} alt="Следующий месяц" />
                        </button>
                    </section>

                    <section className={classes.calendarMonths}>
                        {months.map((monthDate, index) => (
                            <section key={index} className={classes.calendarMonth}>
                                <h3>
                                    {formatMonthLabel(monthDate)}
                                </h3>
                                <section className={classes.calendarWeekdays}>
                                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                                        <section key={day} className={classes.weekday}>{day}</section>
                                    ))}
                                </section>
                                <section className={classes.calendarDays}>
                                    {renderMonth(monthDate)}
                                </section>
                            </section>
                        ))}
                    </section>
                </section>
            }
        </section>
    );
};
